import React, { useState } from 'react';
import { SettingsManagementProps, StaffUser, StaffNotificationSettings } from '../../../types';
import { ProfileForm, SecuritySettings, NotificationSettings, DeliveryPricingManagement, VatManagement } from '../molecules';
import ImageOnCakeManagement from '../molecules/ImageOnCakeManagement';
import ColorPricingManagement from '../molecules/ColorPricingManagement';
import StripeConfigForm from '../molecules/StripeConfigForm';
import './SettingsManagement.css';

type SettingsTab = 'profile' | 'security' | 'notifications' | 'delivery' | 'vat' | 'imageOnCake' | 'colorPricing' | 'payments';

const SettingsManagement: React.FC<SettingsManagementProps> = ({
  user,
  loading = false,
  onUpdateProfile,
  onUpdateSecurity,
  onPasswordReset,
  onUpdateNotifications,
  // Delivery pricing props
  deliveryTiers = [],
  deliverySettings,
  onCreateDeliveryTier,
  onUpdateDeliveryTier,
  onDeleteDeliveryTier,
  onUpdateDeliverySettings,
  // VAT settings props
  vatSettings,
  onUpdateVatSettings,
  // Image on cake settings props
  imageOnCakeSettings,
  onUpdateImageOnCakeSettings
}) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  
  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
  };
  
  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <ProfileForm
            user={user}
            onUpdate={onUpdateProfile}
            loading={loading}
          />
        );
      case 'security':
        return (
          <SecuritySettings
            user={user}
            onUpdate={onUpdateSecurity}
            onPasswordReset={onPasswordReset}
            loading={loading}
          />
        );
      case 'notifications':
        return (
          <NotificationSettings
            settings={user.notificationSettings}
            onUpdate={onUpdateNotifications}
            loading={loading}
          />
        );
      case 'delivery':
        return (
          <DeliveryPricingManagement
            tiers={deliveryTiers}
            settings={deliverySettings || null}
            loading={loading}
            onCreateTier={onCreateDeliveryTier || (() => {})}
            onUpdateTier={onUpdateDeliveryTier || (() => {})}
            onDeleteTier={onDeleteDeliveryTier || (() => {})}
            onUpdateSettings={onUpdateDeliverySettings || (() => {})}
          />
        );
      case 'vat':
        return (
          <VatManagement
            settings={vatSettings || null}
            loading={loading}
            onUpdateSettings={onUpdateVatSettings || (() => {})}
          />
        );
      case 'imageOnCake':
        return (
          <ImageOnCakeManagement
            settings={imageOnCakeSettings || null}
            loading={loading}
            onUpdateSettings={onUpdateImageOnCakeSettings || (() => {})}
          />
        );
      case 'colorPricing':
        return <ColorPricingManagement />;
      case 'payments':
        return (
          <StripeConfigForm
            onConfigUpdated={(config) => {
              console.log('Stripe configuration updated:', config);
            }}
          />
        );
      default:
        return null;
    }
  };
  
  return (
    <div className="settings-management">
      <div className="settings-header">
        <h2>Account Settings</h2>
      </div>
      
      <div className="settings-container">
        <div className="settings-sidebar">
          <ul className="settings-tabs">
            <li 
              className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => handleTabChange('profile')}
            >
              Profile Information
            </li>
            <li 
              className={`settings-tab ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => handleTabChange('security')}
            >
              Security & Permissions
            </li>
            <li 
              className={`settings-tab ${activeTab === 'notifications' ? 'active' : ''}`}
              onClick={() => handleTabChange('notifications')}
            >
              Notification Preferences
            </li>
            <li 
              className={`settings-tab ${activeTab === 'delivery' ? 'active' : ''}`}
              onClick={() => handleTabChange('delivery')}
            >
              Delivery Pricing
            </li>
            <li 
              className={`settings-tab ${activeTab === 'vat' ? 'active' : ''}`}
              onClick={() => handleTabChange('vat')}
            >
              VAT Management
            </li>
            <li 
              className={`settings-tab ${activeTab === 'imageOnCake' ? 'active' : ''}`}
              onClick={() => handleTabChange('imageOnCake')}
            >
              Image on Cake Pricing
            </li>
            <li 
              className={`settings-tab ${activeTab === 'colorPricing' ? 'active' : ''}`}
              onClick={() => handleTabChange('colorPricing')}
            >
              Colour Pricing
            </li>
            <li 
              className={`settings-tab ${activeTab === 'payments' ? 'active' : ''}`}
              onClick={() => handleTabChange('payments')}
            >
              Payment Settings
            </li>
          </ul>
        </div>
        
        <div className="settings-content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default SettingsManagement;
